import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PlusCircle, ArrowUpRight, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { AGENT_ID_TO_ROUTE, AGENT_DISPLAY_NAMES, AGENT_ICONS } from "@/lib/agent-ids";
import {
  agentIconMap,
  phaseTone,
  pipelineStatusLabel,
  pipelineStatusTone,
  pipelineToneLabel,
  pipelineToneStyle,
  PIPELINE_TONES,
  type PipelineTone,
} from "@/lib/pipeline-utils";
import type { AgentPipelineEntry } from "@/types/api";
import { useTranslation } from "react-i18next";

interface AgentWorkflowDAGProps {
  agents: AgentPipelineEntry[];
  projectId: string;
}

const PHASES = [
  { key: "discover", label: "Discover", num: "01" },
  { key: "prepare", label: "Prepare", num: "02" },
  { key: "explore", label: "Explore", num: "03" },
  { key: "realize", label: "Realize", num: "04" },
  { key: "deploy", label: "Deploy", num: "05" },
] as const;

// Descrição curta exibida em cada card de agente.

const AGENT_DESC_KEY: Record<string, string> = {
  // Descreve o que o agente faz, não o que esta run produziu: a copy anterior
  // cravava contagens de RFC e de TCodes que pareciam medidas e não eram, e a
  // promessa de BOM no passado ficou falsa com a divisão em duas fases (#854),
  // porque quem gera BOM é a entrega, depois da aprovação do escopo. Sem
  // número aqui não há o que divergir do catálogo, que é o que a trava de
  // paridade em tcode-catalog-parity.test.ts cobra.
  functional: "License assessment: ERP extraction, Fit-to-Standard and BOM.",
  architect: "Clean Core decisions per customization, with effort and cost.",
};

function PhaseHead({
  num,
  label,
  phaseKey,
  agentCount,
  progress,
  tone,
  pct,
}: {
  num: string;
  label: string;
  phaseKey: string;
  agentCount: number;
  progress: string;
  tone: PipelineTone | "empty";
  pct: number;
}) {
  const { t } = useTranslation();
  // Fase vazia não é estado do pipeline: fica neutra em vez de emprestar a cor
  // de "aguardando", que diria que existe algo para acontecer ali.
  const style = tone === "empty" ? null : pipelineToneStyle[tone];
  return (
    <div
      className="px-0.5 pt-1.5 pb-3 relative"
      data-testid={`phase-head-${phaseKey}`}
      data-tone={tone}
    >
      <div className="font-mono text-[10px] text-[#b2bccb] tracking-[0.1em] mb-1">{num}</div>
      <div
        className={cn(
          "font-brand font-extrabold text-[15px] tracking-[0.08em] uppercase",
          style ? style.text : "text-[#b2bccb]",
        )}
      >
        {label}
      </div>
      <div className="text-[10px] text-[#7b8799] font-mono tracking-[0.08em] mt-0.5 uppercase">
        {agentCount === 0 ? t("No agents") : `${agentCount} ${t(agentCount === 1 ? "agent" : "agents")} · ${progress}`}
      </div>
      <div className="mt-2.5 h-[3px] rounded bg-[#e8ebf0] overflow-hidden">
        <div
          className="h-full rounded"
          style={{ width: `${pct}%`, background: style ? style.bar : "#e8ebf0" }}
        />
      </div>
    </div>
  );
}

function AgentCard({
  agent,
  projectId,
  index,
}: {
  agent: AgentPipelineEntry;
  projectId: string;
  index: number;
}) {
  const { t } = useTranslation();
  const routeSlug = AGENT_ID_TO_ROUTE[agent.agent_id] || agent.agent_id;
  const displayName = AGENT_DISPLAY_NAMES[agent.agent_id] || agent.name;
  const tone = pipelineStatusTone[agent.pipeline_status];
  const label = pipelineStatusLabel(agent.pipeline_status, t);
  const Icon = agentIconMap[AGENT_ICONS[agent.agent_id]] || agentIconMap.Calculator;
  const isDone = tone === "done";
  // **Estado**, não tom. `isRunning` lia `tone === "running"` enquanto `ready`
  // também tinha esse tom, e era daí que saía o card com badge `Running` e
  // rodapé `READY TO RUN` ao mesmo tempo.
  const isRunning = agent.pipeline_status === "running";
  const isNext = tone === "next";

  const cardClasses = cn(
    "group relative flex flex-col gap-2.5 rounded-[10px] p-3.5 cursor-pointer transition-all duration-200",
    "hover:-translate-y-px",
    isDone && "border border-[rgba(39,161,85,0.25)]",
    isRunning && "border border-[rgba(255,200,61,0.5)]",
    isNext && "border border-[rgba(29,78,160,0.35)]",
    !isDone && !isRunning && !isNext && "bg-[#fafbfc] border border-[#e8ebf0] hover:border-[#08254c] hover:shadow-[0_4px_12px_rgba(8,37,76,0.08)]",
  );

  const cardStyle: React.CSSProperties | undefined = isDone
    ? { background: "linear-gradient(180deg,#f6faf8 0%,#fff 60%)" }
    : isRunning
      ? { background: "linear-gradient(180deg,#fffdf5 0%,#fff 50%)", boxShadow: "0 0 0 3px rgba(255,200,61,0.08)" }
      : isNext
        ? { background: "linear-gradient(180deg,#f6f8fc 0%,#fff 55%)" }
        : undefined;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link to={`/projects/${projectId}/agents/${routeSlug}`} className={cardClasses} style={cardStyle}>
        <div className="flex justify-between items-start gap-2">
          <div
            className={cn(
              "w-[38px] h-[38px] rounded-[9px] flex items-center justify-center flex-shrink-0 border",
              isDone && "bg-[rgba(39,161,85,0.1)] border-[rgba(39,161,85,0.2)]",
              isRunning && "bg-[rgba(255,200,61,0.18)] border-[rgba(255,200,61,0.4)]",
              !isDone && !isRunning && "bg-[rgba(8,37,76,0.06)] border-transparent",
            )}
          >
            <Icon
              className={cn(
                "w-[18px] h-[18px]",
                isDone && "text-[#27a155]",
                isRunning && "text-[#b57a00]",
                isNext && "text-[#1d4ea0]",
                !isDone && !isRunning && !isNext && "text-[#08254c]",
              )}
              strokeWidth={1.75}
            />
          </div>
          <StatusPill label={label} tone={tone} />
        </div>
        <div>
          <div
            className={cn(
              "font-mono text-[10px] tracking-[0.12em] font-semibold",
              isRunning ? "text-[#b57a00]" : "text-[#7b8799]",
            )}
          >
            #{String(agent.order + 1).padStart(2, "0")} · {t("agent").toLocaleUpperCase()}
          </div>
          <div className="font-semibold text-sm text-[#08254c] tracking-[-0.005em] leading-[1.25] mt-0.5">
            {displayName}
          </div>
        </div>
        <div className="text-[11px] text-[#596b85] leading-[1.45]">
          {AGENT_DESC_KEY[agent.agent_id] ? t(AGENT_DESC_KEY[agent.agent_id]) : agent.name}
        </div>
        <div className="flex justify-between items-center gap-2 pt-2 border-t border-[#e8ebf0] mt-0.5">
          {/* O rodapé repete o rótulo do badge, sempre. Antes ele tinha texto
              próprio (`READY TO RUN`, `COMPLETE`) escolhido por tom, o que
              permitia rodapé e badge afirmarem estados diferentes no mesmo
              card. Derivando do mesmo rótulo, a contradição deixa de ser
              representável. */}
          <span className="font-mono text-[10px] text-[#7b8799] tracking-[0.06em] uppercase">
            {agent.phase.toUpperCase()} · {label.toUpperCase()}
          </span>
          {isRunning ? (
            <Play className="w-[13px] h-[13px] text-[#b57a00]" />
          ) : (
            <ArrowUpRight className="w-[13px] h-[13px] text-[#b2bccb] group-hover:text-[#08254c]" />
          )}
        </div>
      </Link>
    </motion.div>
  );
}

function StatusPill({ label, tone }: { label: string; tone: PipelineTone }) {
  const style = pipelineToneStyle[tone];
  return (
    <span
      className={cn(
        "font-mono text-[10px] font-semibold tracking-[0.1em] uppercase flex items-center gap-1.5 px-2 py-0.5 rounded-full",
        style.pill,
      )}
    >
      <span
        className={cn("w-[5px] h-[5px] rounded-full", tone === "running" && "mg-pulse")}
        style={{
          background: style.dot,
          ...(style.glow ? { boxShadow: `0 0 6px ${style.dot}` } : {}),
        }}
      />
      {label}
    </span>
  );
}

/** Legenda derivada da própria escala: não há como discordar dos badges. */
function PipelineLegend() {
  const { t } = useTranslation();
  return (
    <div
      className="flex gap-4 flex-wrap text-[11px] text-[#596b85] font-mono tracking-[0.04em]"
      data-testid="pipeline-legend"
    >
      {PIPELINE_TONES.map((tone) => {
        const style = pipelineToneStyle[tone];
        return (
          <span key={tone} className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full"
              style={{
                background: style.dot,
                ...(style.glow ? { boxShadow: `0 0 6px ${style.dot}` } : {}),
              }}
            />
            {pipelineToneLabel(tone, t).toUpperCase()}
          </span>
        );
      })}
    </div>
  );
}

export function AgentWorkflowDAG({ agents, projectId }: AgentWorkflowDAGProps) {
  const { t } = useTranslation();
  const phaseGroups = PHASES.map((phase) => ({
    ...phase,
    agents: [...agents]
      .filter((a) => a.phase.toLowerCase() === phase.key)
      .sort((a, b) => a.order - b.order),
  }));

  return (
    <section>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] text-[#ffc83d] tracking-[0.22em] uppercase font-semibold mb-1.5">
            {t("THE PLATFORM")}
          </div>
          <h2 className="font-brand font-bold text-[22px] text-[#08254c] m-0 tracking-[-0.015em] flex items-center gap-3 flex-wrap">
            {t("Agent Pipeline")}
            <span
              className="font-mono text-[10px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-[0.08em]"
              style={{ background: "rgba(255,200,61,0.12)", color: "#8a6200", border: "1px solid rgba(255,200,61,0.3)" }}
            >
              {agents.filter((a) => a.pipeline_status === "completed").length} / {agents.length} {t("Agents")}
            </span>
          </h2>
          <div className="text-[13px] text-[#596b85] mt-1">
            {t("Specialist agents sequenced by SAP Activate phase. Click a card to open the agent workbench.")}
          </div>
        </div>
        <PipelineLegend />
      </div>

      <div className="bg-white border border-[#e8ebf0] rounded-xl p-5 pt-5 pb-7 shadow-[0_1px_2px_rgba(8,37,76,0.04)] relative overflow-x-auto">
        <div className="grid grid-cols-5 gap-[18px] min-w-[960px]">
          {phaseGroups.map((group, gi) => {
            const done = group.agents.filter((a) => a.pipeline_status === "completed").length;
            const total = group.agents.length || 1;
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            // Resumo e cor saem do **mesmo** tom. Antes eram duas derivações
            // independentes: o rótulo tinha vocabulário próprio (`complete`,
            // `ready`, e dois deles sequer passavam por `t()`) e a cor vinha do
            // nome da fase — daí DISCOVER e PREPARE com o mesmo estado e cores
            // diferentes.
            const tone = phaseTone(group.agents);
            const progressLabel = tone === "empty" ? "" : pipelineToneLabel(tone, t);

            return (
              <div key={group.key} className="flex flex-col min-w-0 relative">
                {gi > 0 && (
                  <span
                    aria-hidden="true"
                    className="absolute w-2.5 h-2.5 border-t-[1.5px] border-r-[1.5px] border-[#d4dae3] rotate-45 z-[1]"
                    style={{ left: "-14px", top: "80px" }}
                  />
                )}
                <PhaseHead
                  num={group.num}
                  label={group.label}
                  phaseKey={group.key}
                  agentCount={group.agents.length}
                  progress={progressLabel}
                  tone={tone}
                  pct={pct}
                />

                <div className="flex flex-col gap-2.5">
                  {group.agents.map((agent, ai) => (
                    <AgentCard key={agent.agent_id} agent={agent} projectId={projectId} index={gi * 2 + ai} />
                  ))}
                  {group.agents.length === 0 && (
                    <div className="rounded-[10px] border border-dashed border-[#e0e6ed] px-3 py-6 text-center flex flex-col items-center gap-1.5 min-h-[140px] justify-center">
                      <PlusCircle className="w-[18px] h-[18px] text-[#b2bccb] mb-1" />
                      <div className="text-xs text-[#7b8799] font-medium">{t("No agent yet")}</div>
                      <div className="text-[10px] text-[#b2bccb] font-mono tracking-[0.04em] leading-[1.4]">
                        {t("Specialists appear as the project advances.")}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
